import { ChangeDetectorRef, Component, Input, OnChanges } from '@angular/core';
import { Router } from '@angular/router';
import { ICalculatorDetail } from '@app/core/helpers/calculator-detail.helper';
import { CalculatorProBuilderHelper } from '@app/core/helpers/calculator-pro-builder.helper';
import { MarketComparatorHelper } from '@app/core/helpers/market-comparator.helper';
import { System } from '@app/core/resources/system';
import { CurrencyPiorityService } from '@app/core/services/currency-piority.service';
import { SystemsUpdatedService } from '@app/transactions/services/systems-updated.service';
import { Transaction } from '@app/transactions/transaction';

@Component({
    selector: 'app-currency-comparison',
    templateUrl: './currency-comparison.component.html',
    styleUrls: ['./currency-comparison.component.scss']
})
export class CurrencyComparisonComponent implements OnChanges {
    @Input() public transaction: Transaction = new Transaction();
    public sameSystem: boolean = false;
    public positiveDistinctSystem: boolean = false;
    public negativeDistinctSystem: boolean = false;
    public distinctSystem: boolean = false;
    public positionSystem: number = 0;
    public market: string | undefined = '';
    public marketQuotationValue: number = 0;
    public alternateSystem: System = new System();
    public system: System = new System();
    public comparisonQuotationMarketPercent: number = 0;
    public comparisonQuotationMarket: string = '';
    public percentQuotationMarket: number = 0;
    private calculatorProBuilderHelper: CalculatorProBuilderHelper = new CalculatorProBuilderHelper();

    public constructor(
        private currencyPriorityService: CurrencyPiorityService,
        private router: Router,
        private changeDetectorRef: ChangeDetectorRef,
        public systemsUpdatedService: SystemsUpdatedService
    ) {}

    public ngOnChanges(): void {
        this.systemsUpdatedService.transactionUpdatedSource.asObservable().subscribe(() => {
            this.updateMarket();
            this.updateComparisonValues();

            this.alternateSystem = this.determineAlternateSystem();

            this.setPositionSystem();
            this.setValue(this.comparisonQuotationMarketPercent);

            this.updateSystemComparisons();
            this.changeDetectorRef.detectChanges();
        });
    }

    public setValue(comparisonQuotationMarketPercent: number): void {
        if (!this.alternateSystem || !this.alternateSystem.relationships.currency || !this.alternateSystem.relationships.currency.data) {
            return;
        }
        let priceSystem: number | undefined = this.alternateSystem.relationships.currency.data.attributes.price;
        if (priceSystem !== undefined) {
            this.marketQuotationValue = Math.abs((comparisonQuotationMarketPercent * priceSystem) / 100);
        }
    }

    private updateComparisonValues(): void {
        this.currencyPriorityService.callbacksAndReturnByCurrency(
            this.transaction.relationships.system1.data ?? new System(),
            this.transaction.relationships.system2.data ?? new System(),
            () => this.setComparisonValues(2),
            () => this.setComparisonValues(3)
        );
        this.changeDetectorRef.detectChanges();
    }

    private setComparisonValues(position: number): void {
        this.system =
            position === 2
                ? this.transaction.relationships.system1.data ?? new System()
                : this.transaction.relationships.system2.data ?? new System();

        let positionDetails: ICalculatorDetail | null = this.calculatorProBuilderHelper
            .setTransaction(this.transaction)
            .getPositionNumber(position);

        this.comparisonQuotationMarketPercent = positionDetails?.comparisonQuotationMarketPercent ?? 0;
        this.comparisonQuotationMarket = positionDetails?.comparisonQuotationMarket ?? '';
    }

    private determineAlternateSystem(): System {
        return (
            (this.transaction.relationships.system1.data === this.system
                ? this.transaction.relationships.system2.data
                : this.transaction.relationships.system1.data) ?? new System()
        );
    }

    private setPositionSystem(): void {
        this.positionSystem = this.transaction.relationships.system1.data === this.system ? 1 : 2;
    }

    private updateMarket(): void {
        if (!this.transaction.relationships.system1 || !this.transaction.relationships.system2) {
            return;
        }
        this.market = new MarketComparatorHelper().getMarketByCurrencies(
            this.transaction.relationships.system1.data ?? new System(),
            this.transaction.relationships.system2.data ?? new System()
        );
    }

    private updateSystemComparisons(): void {
        this.sameSystem = this.comparisonQuotationMarketPercent === 0;

        this.distinctSystem =
            this.transaction.relationships.system1.data?.attributes.currency !==
                this.transaction.relationships.system2.data?.attributes.currency && this.comparisonQuotationMarket !== '';

        this.percentQuotationMarket =
            Math.sign(this.comparisonQuotationMarketPercent) === -1
                ? this.comparisonQuotationMarketPercent * -1
                : this.comparisonQuotationMarketPercent;

        if (this.distinctSystem) {
            this.positiveDistinctSystem = this.comparisonQuotationMarketPercent > 0;
            this.negativeDistinctSystem = this.comparisonQuotationMarketPercent < 0;
        }
    }

    public goToCurrencyComparison(): void {
        this.router.navigate(['/cobrar/payoneer-a-banco/'], { fragment: 'section2' });
    }

    public goToPublicDeals(): void {
        this.router.navigate(['/a/banco/palpal'], { fragment: '#publicDeals' });
    }
}
